import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from './SEO';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import FloatingActions from './layout/FloatingActions';
import ImageSlider from "./ui/ImageSlider";
import { seoConfig } from '../data/seoData';
import { businessFAQs } from '../data/faqsData';
import { businessPrograms, businessTimeline } from '../data/coursesData';
import useFavicon from '../hooks/useFavicon';
import LazyImage from './ui/LazyImage';
import LazyVideo from './ui/LazyVideo';

// Bespoke premium rotating words animation for the Business Lab Hero Heading
function RotatingHeading() {
  const words = ["Future Of Business", "Startup Growth", "Market Valuation", "Venture Roadmap"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#FFF5CC] bg-clip-text text-transparent font-extrabold tracking-tight min-w-[150px] min-[360px]:min-w-[260px] min-[420px]:min-w-[320px] sm:min-w-[420px] md:min-w-[560px]">
      {subText}
      <span className="border-r-[3.5px] border-white ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

export default function BusinessLab() {
  useFavicon('/business-lab-favicon/web-app-manifest-512x512.png');
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // Business Budget Estimator States
  const [servicesNeeded, setServicesNeeded] = useState({
    businessPlan: true,
    branding: false,
    website: false,
    pitchDeck: false
  });
  const [scopeSize, setScopeSize] = useState('standard'); // basic, standard, premium

  const toggleFAQ = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleCheckboxChange = (service) => {
    setServicesNeeded(prev => ({ ...prev, [service]: !prev[service] }));
  };

  // Bespoke price calculation formula
  const calculateEstimate = () => {
    let base = 0;
    if (servicesNeeded.businessPlan) base += 15000;
    if (servicesNeeded.branding) base += 10000;
    if (servicesNeeded.website) base += 25000;
    if (servicesNeeded.pitchDeck) base += 12000;

    let multiplier = 1.0;
    if (scopeSize === 'basic') multiplier = 0.8;
    if (scopeSize === 'premium') multiplier = 1.5;

    return Math.floor(base * multiplier);
  };

  return (
    <React.Fragment>
      <SEO {...seoConfig.businessLab} faqData={businessFAQs} />

      <div className="font-inter bg-nm-light min-h-screen text-[#1E293B]">
        {/* Unified sticky nav header */}
        <Navbar />

        {/* Hero Section */}
        <section className="relative overflow-hidden w-full min-h-[90vh] flex items-center py-20 lg:py-24 bg-nm-dark text-[#0B1C2D] border-b border-slate-200/20" aria-label="Hero Section">
          {/* Responsive Brand Video Background */}
          <LazyVideo
            src={isMobile ? "/videos/business-mobile.mp4" : "/videos/business-desktop.mp4"}
            poster="/logo-business.webp"
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.25]"
            placeholderBg="bg-transparent"
          />

          {/* Background glows */}
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#D4AF37]/10 rounded-full blur-[130px] animate-sunrise-shift pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#00C2A8]/5 rounded-full blur-[120px] animate-pulse-slow pointer-events-none" style={{ animationDelay: '4s' }} />

          {/* grid lines overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(to right, black 1px, transparent 1px), linear-gradient(to bottom, black 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />

          <div className="relative max-w-[1500px] mx-auto px-6 w-full flex flex-col items-center justify-center min-h-[80vh] relative z-20 py-20">
            {/* Centered Content Column */}
            <div className="relative z-20 text-center max-w-4xl mx-auto flex flex-col items-center animate-slide-up">
              {/* Header Badge */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <LazyImage
                  src="/business-lab-favicon/apple-touch-icon.png"
                  alt="Business Lab Logo"
                  className="w-14 h-14 rounded-2xl object-cover border border-slate-200/20 shadow-2xl bg-[#0B1020]"
                  fetchPriority="high"
                />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-dark text-xs font-semibold text-[#0B1C2D]">
                  <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
                  <span>For Entrepreneurs, Startups & Businesses</span>
                </div>
              </div>

              {/* Centered Heading */}
              <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-[#0B1C2D] tracking-tight mb-8">
                <span className="block">Build The</span>
                <span className="block mt-2"><RotatingHeading /></span>
              </h1>

              {/* Centered Description */}
              <p className="text-slate-700 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10 font-light">
                A premium innovation ecosystem empowering startup founders, local companies, and entrepreneurs with strategic planning, digital assets, branding, and capital readiness.
              </p>

              {/* Centered CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full max-w-md">
                <a
                  href="/book-demo?vertical=business"
                  className="px-8 py-4.5 rounded-2xl glass-button-dark text-[#D4AF37] font-bold text-center w-full sm:w-auto"
                >
                  Book Free Consultation
                </a>
                <a
                  href="#business"
                  className="px-8 py-4.5 rounded-2xl glass-button-dark text-slate-700 font-bold text-center w-full sm:w-auto"
                >
                  Explore Services
                </a>
              </div>

              {/* Centered stats */}
              <div className="flex flex-wrap justify-center gap-6 w-full">
                {["360° Consulting", "VC Pitch Prep", "GTM Execution"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 glass-card-dark px-5 py-3 rounded-2xl hover:scale-105 transition-all duration-300">
                    <div className="w-8 h-8 rounded-full nm-inset-dark flex items-center justify-center text-[#D4AF37] font-bold text-sm">✓</div>
                    <span className="text-sm font-semibold text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= RELATABLE MARQUEE loop ================= */}
        <section className="bg-nm-light border-y border-slate-200/40 py-8 relative overflow-hidden" aria-label="Business Lab Trust Indicators">
          <div className="max-w-7xl mx-auto px-6 mb-4 text-center">
            <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest">Our Business & Incubation Excellence</span>
          </div>

          <div className="relative flex overflow-hidden w-full select-none py-2">
            {/* First Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop">
              {businessMarquee.map((logo, idx) => (
                <div key={idx} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
            {/* Second Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop" aria-hidden="true">
              {businessMarquee.map((logo, idx) => (
                <div key={`dup-${idx}`} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Vision & Mission Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 mb-20">
              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl nm-inset-light flex items-center justify-center text-[#C6A25E] text-2xl mb-6">👁️</div>
                <h3 className="text-xl font-bold mb-3 text-[#0B1C2D]">Our Vision</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-light">
                  To empower aspiring entrepreneurs and startup founders with the knowledge, strategic models, and digital resources needed to build successful, sustainable ventures that create lasting societal value.
                </p>
              </div>

              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:scale-[1.01] transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl nm-inset-light flex items-center justify-center text-[#D4AF37] text-2xl mb-6">🎯</div>
                <h3 className="text-xl font-bold mb-3 text-[#0B1C2D]">Our Mission</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-light">
                  Our mission is to provide comprehensive startup advisory frameworks. We connect builders with experienced mentors and provide custom web development setups to launch high-performance business applications.
                </p>
              </div>
            </div>

            {/* Founder Message */}
            <div className="nm-flat-light rounded-3xl p-8 md:p-12 border-transparent text-center max-w-4xl mx-auto shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#C6A25E]/10 to-transparent rounded-full blur-2xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-amber-500/5 to-transparent rounded-full blur-2xl pointer-events-none" />

              <div className="relative z-10 flex flex-col items-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full nm-inset-light shadow-sm mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] animate-pulse" />
                  <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Founder's Mission Statement</span>
                </div>

                <div className="w-20 h-20 rounded-full overflow-hidden mb-6 border-2 border-[#C6A25E] shadow-md relative group">
                  <LazyImage src="/founder.webp" alt="Mohd. Yaasir Sufiyan" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>

                <blockquote className="text-lg md:text-xl font-playfair font-medium text-[#0B1C2D] italic max-w-3xl leading-relaxed">
                  "Entrepreneurship is the bridge between innovation and impact. At Business Lab, we're not just teaching business skills—we're helping visionary leaders create solutions to real-world problems."
                </blockquote>

                <div className="mt-6">
                  <h4 className="text-base font-bold text-[#0B1C2D]">Mohd. Yaasir Sufiyan</h4>
                  <p className="text-xs font-bold text-[#C6A25E] uppercase tracking-widest mt-1">Founder, Cervello Eduverse</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Business Programs Section */}
        <section id="business" className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Business <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">Programs</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Comprehensive entrepreneurship modules, product advisory, and coding support.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {businessPrograms.map((item, index) => (
                <div
                  key={index}
                  className="group nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between h-full"
                >
                  <Link to={`/course/${getSlug(item.title)}`} className="relative aspect-video w-full overflow-hidden bg-slate-100 block">
                    <ImageSlider
                      images={item.images}
                      alt={item.title}
                      className="w-full h-full"
                    />
                  </Link>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">BUSINESS SERVICE</span>
                      <Link to={`/course/${getSlug(item.title)}`}>
                        <h3 className="text-xl font-bold text-[#0B1C2D] mt-1 mb-2 hover:text-[#C6A25E] transition-colors">{item.title}</h3>
                      </Link>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light line-clamp-2">{item.description}</p>
                    </div>

                    <div>
                      <ul className="space-y-2 border-t border-slate-200/30 pt-4 mb-6">
                        {item.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={`/course/${getSlug(item.title)}`}
                        className="w-full nm-button-light py-3 rounded-xl font-semibold border-transparent text-[#0B1C2D] text-center block"
                      >
                        View Service Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Business Budget Estimator Calculator */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Project Cost <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">Estimator</span>
              </h2>
              <p className="text-slate-500 text-sm max-w-lg mx-auto">
                Select custom capabilities to generate a dynamic structural roadmap estimate instantly.
              </p>
            </div>

            <div className="nm-flat-light p-8 rounded-3xl border-transparent">
              {/* Checkboxes */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { id: 'businessPlan', label: 'Business Strategy & Planning (₹15K)' },
                  { id: 'branding', label: 'Custom Branding & Logo (₹10K)' },
                  { id: 'website', label: 'Bespoke Startup Website (₹25K)' },
                  { id: 'pitchDeck', label: 'Investor Pitch Deck Pitch (₹12K)' }
                ].map((item) => (
                  <label key={item.id} className="flex items-center space-x-3 p-3 nm-inset-light border-transparent rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={servicesNeeded[item.id]}
                      onChange={() => handleCheckboxChange(item.id)}
                      className="rounded border-slate-300 text-[#C6A25E] focus:ring-[#C6A25E] w-4 h-4"
                    />
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                  </label>
                ))}
              </div>

              {/* Scope Size Options */}
              <div className="mb-8">
                <span className="block text-xs font-bold text-[#0B1C2D] uppercase tracking-wider mb-3">Project Scope Complexity</span>
                <div className="flex gap-4">
                  {[
                    { id: 'basic', label: 'Basic MVP (0.8x)' },
                    { id: 'standard', label: 'Standard Scaling (1.0x)' },
                    { id: 'premium', label: 'Full Enterprise (1.5x)' }
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setScopeSize(option.id)}
                      className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer ${scopeSize === option.id
                        ? 'nm-flat-light text-[#D4AF37] font-bold border-transparent'
                        : 'nm-button-light text-slate-600 border-transparent'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Display Calculated Price */}
              <div className="pt-6 border-t border-slate-200/30 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Estimated Investment</span>
                  <div className="text-3xl font-extrabold text-[#0B1C2D] mt-1">₹{calculateEstimate().toLocaleString('en-IN')}*</div>
                </div>
                <a
                  href={`/book-demo?vertical=business&estimate=${calculateEstimate()}`}
                  className="nm-button-light text-[#C6A25E] font-bold border-transparent px-8 py-3.5 rounded-xl text-center active:scale-95 transition-all"
                >
                  Request Consultation
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Coming Soon Alternating Node Timeline */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-20">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Coming <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">Soon</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Exciting new frameworks launching throughout our upcoming roadmap.
              </p>
            </div>

            {/* Alternating Node timeline */}
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-[#C6A25E] to-[#D4AF37]" />

              <div className="space-y-12">
                {businessTimeline.map((item, index) => {
                  const isEven = index % 2 === 0;
                  return (
                    <div key={index} className={`flex items-start ${isEven ? 'flex-row' : 'flex-row-reverse'} gap-4 md:gap-8`}>
                      <div className={`w-5/12 ${isEven ? 'text-right' : 'text-left'}`}>
                        <span className="inline-block bg-[#C6A25E] text-white text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-lg">
                          {item.date}
                        </span>

                        <div className="mt-4 nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.01] transition-all">
                          <div className="text-3xl mb-3">{item.icon}</div>
                          <h4 className="text-lg font-bold text-[#0B1C2D] mb-2">{item.title}</h4>
                          <p className="text-slate-500 text-xs leading-relaxed font-light">{item.description}</p>
                        </div>
                      </div>

                      {/* Node Center Badge */}
                      <div className="w-2/12 flex justify-center mt-2">
                        <div className="w-6 h-6 rounded-full bg-white border-4 border-[#C6A25E] flex items-center justify-center z-10">
                          <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                        </div>
                      </div>

                      <div className="w-5/12" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Business Lab <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">FAQ</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Frequently asked queries about our entrepreneur incubation and acceleration modules.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {businessFAQs.map((faq, index) => {
                const isOpen = activeFaq === index;
                return (
                  <div key={index} className="nm-flat-light rounded-2xl border-transparent overflow-hidden">
                    <button
                      className="w-full text-left p-6 flex justify-between items-center bg-transparent focus:outline-none"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="font-bold text-base sm:text-lg text-[#0B1C2D]">{faq.question}</h3>
                      <span className={`text-[#C6A25E] text-xl transition-transform duration-300 transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                        ▾
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-60 opacity-100 border-t border-slate-200/30' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                    >
                      <div className="p-6 text-slate-600 text-sm leading-relaxed nm-inset-light">
                        {faq.answer && faq.answer.listItems ? (
                          <div>
                            <p className="mb-2 font-semibold">{faq.answer.text}</p>
                            <ul className="list-disc pl-5 space-y-1">
                              {faq.answer.listItems.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>{faq.answer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Unified footer & FABs */}
        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}

// Business Lab marquee dataset
const businessMarquee = [
  {
    name: "Startup Incubation",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  },
  {
    name: "VC Pitch Preparation",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    )
  },
  {
    name: "Business Planning",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  {
    name: "Market Feasibility",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    )
  },
  {
    name: "GTM Strategy",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    )
  },
  {
    name: "SOP & Operations Scale",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  },
  {
    name: "Corporate Workshops",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  },
  {
    name: "Product Mockup Audits",
    svg: (
      <svg className="w-8 h-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )
  }
];